/* SEQ Air Care — Priority suburb landing page */
'use strict';

(() => {
  const ROOT_SELECTOR = '[data-seq-suburb-main]';

  function removeDuplicateThemeTitle(root) {
    const suburb = (root.dataset.suburb || '').trim();
    if (!suburb) return;

    const normalise = (value) => value.replace(/\s+/g, ' ').trim().toLowerCase();
    const allowedTitles = new Set([
      normalise(suburb),
      normalise(`Air Conditioner Cleaning ${suburb}`),
      normalise(`Air Con Cleaning ${suburb}`)
    ]);

    document
      .querySelectorAll('h1.main-page-title, .main-page-title, h1.page-title, .page-title, main > h1')
      .forEach((heading) => {
        if (root.contains(heading)) return;
        if (!allowedTitles.has(normalise(heading.textContent || ''))) return;

        heading.hidden = true;
        heading.setAttribute('aria-hidden', 'true');
        heading.style.setProperty('display', 'none', 'important');

        const wrapper = heading.parentElement;
        if (
          wrapper
          && wrapper.children.length === 1
          && !wrapper.matches('header, nav, main, body')
        ) {
          wrapper.style.setProperty('display', 'none', 'important');
        }
      });
  }

  function initFaq(root) {
    const group = root.querySelector('[data-suburb-faq]');
    if (!group || group.dataset.ready === 'true') return;

    group.dataset.ready = 'true';

    group.querySelectorAll('details').forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;

        group.querySelectorAll('details[open]').forEach((other) => {
          if (other !== item) other.removeAttribute('open');
        });
      });
    });
  }

  function initSmoothNavigation(root) {
    root.querySelectorAll('a[href^="#"]').forEach((link) => {
      if (link.dataset.smoothReady === 'true') return;
      link.dataset.smoothReady = 'true';

      link.addEventListener('click', (event) => {
        const id = link.getAttribute('href');
        if (!id || id === '#') return;

        const target = root.querySelector(id) || document.querySelector(id);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start'
        });

        try {
          history.replaceState(null, '', id);
        } catch (error) {
          // Updating the hash is optional.
        }
      });
    });
  }

  function initMiniNav(root) {
    const nav = root.querySelector('[data-suburb-mini-nav]');
    if (!nav || nav.dataset.ready === 'true') return;
    nav.dataset.ready = 'true';

    const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
    const sections = links
      .map((link) => {
        const target = document.querySelector(link.getAttribute('href'));
        return target ? { link, target } : null;
      })
      .filter(Boolean);

    if (!sections.length || !('IntersectionObserver' in window)) return;

    const visible = new Map();

    const setActive = () => {
      const candidates = sections
        .filter(({ target }) => visible.get(target))
        .sort((a, b) => {
          return Math.abs(a.target.getBoundingClientRect().top)
            - Math.abs(b.target.getBoundingClientRect().top);
        });

      const active = candidates[0] || sections.find(({ target }) => {
        const rect = target.getBoundingClientRect();
        return rect.top <= 180 && rect.bottom > 180;
      });

      links.forEach((link) => {
        const isActive = Boolean(active && link === active.link);
        link.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });

      active?.link.scrollIntoView({
        block: 'nearest',
        inline: 'center',
        behavior: 'smooth'
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visible.set(entry.target, entry.isIntersecting);
      });
      setActive();
    }, {
      rootMargin: '-18% 0px -65% 0px',
      threshold: [0, 0.01, 0.25]
    });

    sections.forEach(({ target }) => observer.observe(target));
  }

  function initRevealAnimations(root) {
    const items = Array.from(root.querySelectorAll('[data-suburb-reveal]'));
    if (!items.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    items.forEach((item, index) => {
      if (!item.style.getPropertyValue('--seq-reveal-delay')) {
        item.style.setProperty('--seq-reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);
      }
    });

    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    });

    items.forEach((item) => observer.observe(item));
  }

  function initComparisonSliders(root) {
    root.querySelectorAll('[data-suburb-compare]').forEach((comparison) => {
      if (comparison.dataset.ready === 'true') return;
      comparison.dataset.ready = 'true';

      const range = comparison.querySelector('[data-suburb-compare-range]');
      const before = comparison.querySelector('[data-suburb-compare-before]');
      const line = comparison.querySelector('[data-suburb-compare-line]');
      if (!range || !before || !line) return;

      const update = () => {
        const value = Math.max(0, Math.min(100, Number(range.value) || 50));
        before.style.width = `${value}%`;
        line.style.left = `${value}%`;
        range.setAttribute('aria-valuetext', `${value}% before image visible`);
      };

      range.addEventListener('input', update, { passive: true });
      range.addEventListener('change', update);
      update();
    });
  }

  function initRecentJobSliders(root) {
    const host = root.querySelector('[data-suburb-recent-jobs]');
    if (!host) return;

    host.querySelectorAll('[data-seq-ba-slider]').forEach((slider) => {
      if (slider.dataset.suburbSliderReady === 'true') return;
      slider.dataset.suburbSliderReady = 'true';

      const range = slider.querySelector('[data-seq-ba-range]');
      const before = slider.querySelector('[data-seq-ba-before]');
      const line = slider.querySelector('[data-seq-ba-line]');
      const beforeImage = slider.querySelector('.seq-ba-image--before');

      if (!range || !before || !line) return;

      const syncImageWidth = () => {
        if (!beforeImage) return;
        const width = Math.max(1, Math.round(slider.getBoundingClientRect().width));
        beforeImage.style.width = `${width}px`;
      };

      const update = () => {
        const value = Math.max(0, Math.min(100, Number(range.value) || 50));
        before.style.width = `${value}%`;
        line.style.left = `${value}%`;
        range.setAttribute('aria-valuetext', `${value}% of the before image visible`);
      };

      range.addEventListener('input', update, { passive: true });
      range.addEventListener('change', update);

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(syncImageWidth);
        observer.observe(slider);
      } else {
        window.addEventListener('resize', syncImageWidth, { passive: true });
      }

      syncImageWidth();
      update();
    });
  }

  function truncateJobDescription(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';

    const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
    let result = sentences.slice(0, 2).join(' ').trim();

    if (result.length > 235) {
      result = `${result.slice(0, 232).replace(/\s+\S*$/, '')}…`;
    } else if (sentences.length > 2 && !result.endsWith('…')) {
      result = `${result}…`;
    }

    return result;
  }

  function findJobLocation(card, head, fallback) {
    const explicit = card.querySelector(
      '[data-job-suburb], .seq-job-suburb, .seq-result-card__suburb, [class*="job-location"], [class*="job-suburb"]'
    );

    if (explicit?.textContent.trim()) return explicit.textContent.trim();

    const title = head.querySelector('h3')?.textContent.trim() || '';
    const candidates = Array.from(head.querySelectorAll('span, small, strong, p, b, em'))
      .filter((element) => !element.closest('h3'))
      .map((element) => element.textContent.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .filter((value) => value !== title)
      .filter((value) => value.length <= 38)
      .filter((value) => !/\d/.test(value))
      .filter((value) => !/(featured|job|indoor|outdoor|unit|clean|mould|musty|build-up|buildup|result)/i.test(value));

    return candidates[0] || fallback || 'Local service area';
  }

  function normaliseRecentJobCard(card, fallbackSuburb) {
    if (card.dataset.suburbCardReady === 'true') return;
    card.dataset.suburbCardReady = 'true';
    card.classList.add('seq-suburb-job-card');

    const head = card.querySelector('.seq-result-card__head');
    const media = card.querySelector('[data-seq-ba-slider], .seq-ba-slider');
    const body = card.querySelector('.seq-result-card__body');
    const titleElement = head?.querySelector('h3');

    if (head && titleElement) {
      const location = findJobLocation(card, head, fallbackSuburb);
      const title = titleElement.textContent.replace(/\s+/g, ' ').trim();

      const locationElement = document.createElement('span');
      locationElement.className = 'seq-suburb-job-card__location';
      locationElement.textContent = location;

      const cleanTitle = document.createElement('h3');
      cleanTitle.textContent = title;

      head.replaceChildren(locationElement, cleanTitle);
    }

    if (!body) return;

    /*
      Capture only the approved compact-card content before replacing
      the entire body. Legacy headings, long paragraphs, duplicate links,
      dates and metadata therefore cannot remain visible.
    */
    const sourceDescription =
      body.querySelector(':scope > p')
      || body.querySelector('p');

    const descriptionText = truncateJobDescription(
      sourceDescription?.textContent || ''
    );

    const sourceInsightContainer =
      body.querySelector('.seq-mini-proof')
      || body.querySelector('[class*="insight"]')
      || body.querySelector('[class*="proof"]');

    const insightTexts = sourceInsightContainer
      ? Array.from(sourceInsightContainer.querySelectorAll('span, li'))
          .map((item) => item.textContent
            .replace(/^[✓✔•\s]+/, '')
            .replace(/\s+/g, ' ')
            .trim())
          .filter(Boolean)
          .slice(0, 3)
      : [];

    const links = Array.from(card.querySelectorAll('a[href]'));

    const bookingLink =
      links.find((link) => link.matches('[data-open-booking]'))
      || links.find((link) => /book/i.test(link.textContent));

    const detailLink =
      links.find((link) => link !== bookingLink && /view full job/i.test(link.textContent))
      || links.find((link) => link !== bookingLink && /see what was found|see completed job/i.test(link.textContent))
      || links.find((link) => link !== bookingLink && /recent-job/i.test(link.getAttribute('href') || ''))
      || links.find((link) => link !== bookingLink);

    const cleanDescription = document.createElement('p');
    cleanDescription.className = 'seq-suburb-job-card__description';
    cleanDescription.textContent = descriptionText;

    const cleanInsights = document.createElement('div');
    cleanInsights.className = 'seq-suburb-job-card__insights';

    insightTexts.forEach((text) => {
      const insight = document.createElement('span');
      insight.textContent = text;
      cleanInsights.appendChild(insight);
    });

    const actions = document.createElement('div');
    actions.className = 'seq-suburb-job-card__actions';

    if (bookingLink) {
      bookingLink.hidden = false;
      bookingLink.removeAttribute('aria-hidden');
      bookingLink.className = 'seq-suburb-job-card__button seq-suburb-job-card__button--primary';
      bookingLink.textContent = 'Book a clean like this';
      bookingLink.dataset.bookingSource = 'priority_suburb_recent_job';
      bookingLink.dataset.bookingSuburb = fallbackSuburb || '';
      actions.appendChild(bookingLink);
    }

    if (detailLink) {
      detailLink.hidden = false;
      detailLink.removeAttribute('aria-hidden');
      detailLink.className = 'seq-suburb-job-card__button seq-suburb-job-card__button--secondary';
      detailLink.textContent = 'View full job';
      actions.appendChild(detailLink);
    }

    body.replaceChildren();

    if (descriptionText) {
      body.appendChild(cleanDescription);
    }

    if (cleanInsights.children.length) {
      body.appendChild(cleanInsights);
    }

    if (actions.children.length) {
      body.appendChild(actions);
    }

    /*
      The shared snippet may output additional text, headings or links as
      siblings of the main card body. Rebuild the complete card so only the
      approved header, before/after media and cleaned body can remain.
    */
    const approvedNodes = [head, media, body].filter(Boolean);
    card.replaceChildren(...approvedNodes);
  }

  function initRecentJobsCarousel(root) {
    const host = root.querySelector('[data-suburb-recent-jobs]');
    const grid = host?.querySelector('.seq-result-grid');
    if (!host || !grid || grid.dataset.carouselReady === 'true') return;

    const cards = Array.from(grid.querySelectorAll('.seq-result-card'));
    if (!cards.length) return;

    grid.dataset.carouselReady = 'true';
    grid.setAttribute('role', 'region');
    grid.setAttribute('aria-label', 'Recent job cards');
    grid.setAttribute('tabindex', '0');

    const fallbackSuburb = root.dataset.suburb || '';
    cards.forEach((card) => normaliseRecentJobCard(card, fallbackSuburb));

    const sectionHead = host.querySelector('.seq-section-head');
    const controls = document.createElement('div');
    controls.className = 'seq-suburb-jobs-carousel__controls';
    controls.innerHTML = `
      <span class="seq-suburb-jobs-carousel__count" aria-live="polite">
        <b data-carousel-current>1</b>
        <i aria-hidden="true">/</i>
        <span data-carousel-total>${cards.length}</span>
      </span>
      <div class="seq-suburb-jobs-carousel__buttons">
        <button type="button" data-carousel-prev aria-label="Previous recent job">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
        </button>
        <button type="button" data-carousel-next aria-label="Next recent job">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
        </button>
      </div>
    `;

    if (sectionHead) sectionHead.insertAdjacentElement('afterend', controls);
    else grid.insertAdjacentElement('beforebegin', controls);

    const current = controls.querySelector('[data-carousel-current]');
    const previous = controls.querySelector('[data-carousel-prev]');
    const next = controls.querySelector('[data-carousel-next]');

    const getGap = () => {
      const styles = window.getComputedStyle(grid);
      return Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    };

    const getIndex = () => {
      const left = grid.scrollLeft;
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    };

    const syncControls = () => {
      const index = getIndex();
      if (current) current.textContent = String(index + 1);
      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index >= cards.length - 1;
    };

    const scrollToIndex = (index) => {
      const safeIndex = Math.max(0, Math.min(cards.length - 1, index));
      grid.scrollTo({
        left: cards[safeIndex].offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'
      });
    };

    previous?.addEventListener('click', () => scrollToIndex(getIndex() - 1));
    next?.addEventListener('click', () => scrollToIndex(getIndex() + 1));

    grid.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollToIndex(getIndex() - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollToIndex(getIndex() + 1);
      }
    });

    let scrollTimer = 0;
    grid.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(syncControls, 70);
    }, { passive: true });

    let heightTimer = 0;
    const equaliseCardHeights = () => {
      window.clearTimeout(heightTimer);
      heightTimer = window.setTimeout(() => {
        cards.forEach((card) => card.style.removeProperty('height'));
        const tallest = Math.max(...cards.map((card) => Math.ceil(card.getBoundingClientRect().height)));
        if (Number.isFinite(tallest) && tallest > 0) {
          cards.forEach((card) => {
            card.style.height = `${tallest}px`;
          });
        }
        syncControls();
      }, 80);
    };

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(equaliseCardHeights);
      observer.observe(grid);
    } else {
      window.addEventListener('resize', equaliseCardHeights, { passive: true });
    }

    window.addEventListener('load', equaliseCardHeights, { once: true });
    equaliseCardHeights();
    syncControls();
  }

  function initMobileSticky(root) {
    const sticky = root.querySelector('[data-suburb-mobile-sticky]');
    const finalSection = root.querySelector('.seq-suburb-main__final');
    if (!sticky || sticky.dataset.ready === 'true') return;
    sticky.dataset.ready = 'true';

    let nearFinal = false;

    const sync = () => {
      const modalOpen = document.documentElement.classList.contains('seq-global-booking-open');
      sticky.classList.toggle('is-hidden', nearFinal || modalOpen);
      sticky.setAttribute('aria-hidden', nearFinal || modalOpen ? 'true' : 'false');
    };

    if (finalSection && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        nearFinal = entries.some((entry) => entry.isIntersecting);
        sync();
      }, { threshold: 0.05 });
      observer.observe(finalSection);
    }

    const classObserver = new MutationObserver(sync);
    classObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    window.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  function initAnalytics(root) {
    if (root.dataset.analyticsReady === 'true') return;
    root.dataset.analyticsReady = 'true';

    root.addEventListener('click', (event) => {
      const bookingLink = event.target.closest('[data-open-booking]');
      if (!bookingLink) return;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'seq_suburb_booking_click',
        suburb: root.dataset.suburb || '',
        source: bookingLink.dataset.bookingSource || 'priority_suburb'
      });

      if (typeof window.clarity === 'function') {
        window.clarity('event', 'seq_suburb_booking_click');
      }
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    removeDuplicateThemeTitle(root);
    initFaq(root);
    initSmoothNavigation(root);
    initMiniNav(root);
    initRevealAnimations(root);
    initComparisonSliders(root);
    initRecentJobSliders(root);
    initRecentJobsCarousel(root);
    initMobileSticky(root);
    initAnalytics(root);
  }

  function init(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
